
import * as React from "react";

import { cn } from "@/lib/utils";

interface TableProps extends React.ComponentProps<"table"> {
  readonly containerClassName?: string;
  readonly "aria-label"?: string;
  readonly "aria-labelledby"?: string;
}

function Table({ className, containerClassName, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledby, children, ...props }: Readonly<TableProps>): React.ReactElement {
  return (
    <section data-slot="table-container" className={cn("relative w-full overflow-auto flex-1", containerClassName)} aria-label={ariaLabel || "Data table"}>
      <table data-slot="table" role="table" className={cn("w-full caption-bottom text-sm", className)} aria-label={ariaLabel} aria-labelledby={ariaLabelledby} {...props}>
        {children}
      </table>
    </section>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">): React.ReactElement {
  return <thead data-slot="table-header" className={cn("[&_tr]:border-b bg-background sticky top-0 z-10", className)} {...props} />;
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">): React.ReactElement {
  return <tbody data-slot="table-body" className={cn("[&_tr:last-child]:border-0", className)} {...props} />;
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">): React.ReactElement {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn("bg-muted/50 border-t font-medium [&>tr]:last:border-b-0 sticky bottom-0 z-10", className)}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">): React.ReactElement {
  return (
    <tr
      data-slot="table-row"
      className={cn("hover:bg-muted/50 data-[state=selected]:bg-muted border-b transition-colors", className)}
      {...props}
    />
  );
}

function TableHead({ className, ...props }: React.ComponentProps<"th">): React.ReactElement {
  return (
    <th
      data-slot="table-head"
      scope="col"
      className={cn(
        "text-foreground h-12 px-4 text-left align-middle font-medium whitespace-nowrap [&:has([role=checkbox])]:pr-0 *:[[role=checkbox]]:translate-y-[2px]",
        className,
      )}
      {...props}
    />
  );
}

function TableCell({ className, ...props }: React.ComponentProps<"td">): React.ReactElement {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        "px-4 py-3 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 *:[[role=checkbox]]:translate-y-[2px]",
        className,
      )}
      {...props}
    />
  );
}

function TableCaption({ className, ...props }: React.ComponentProps<"caption">): React.ReactElement {
  return (
    <caption data-slot="table-caption" className={cn("text-muted-foreground mt-4 text-sm", className)} {...props} />
  );
}

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
